import {useEffect, useMemo, useRef, useState} from "react";

import {AgGridReact} from "ag-grid-react";
import {Box} from "@mui/material"

import {cloneDeep, findIndex, isEmpty, isNull, isUndefined, map, replace, size} from "lodash";
import {withTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {format} from "date-fns";

import rpProcurementStatusService from "@plannel/services/rp/rp-procurement-status-service";
import SplitterContainer from "@plannel/components/SplitterContainer";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import {green} from "@mui/material/colors";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import PaginationContainer from "@plannel/components/PaginationContainer";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";
import planConfigService from "@plannel/services/data/plan-config-service";

const CURRENT_DATE = new ZDate();
const INITIAL_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth() + 1, 1);
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 3, 0);
const INITIAL_PERIOD = {start: INITIAL_START_DATE, end: INITIAL_END_DATE};

const MEASURE = CodeUtil.ipRpMeasure;
const { versionStatusCode } = CodeUtil;
const { snackBarMessageColor } = ColorUtil;
const { SIMULATION, PLANNED, RELEASED } = versionStatusCode;

const ProcurementStatusReport = ({t, viewName, title}) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const gridRef = useRef(null);
  const exportColumnDefs = useRef();
  const pageSizes = [10, 20, 50];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  const resultTotalCount = useRef(null);
  const HRCHY_CD_GRP_COL_ID = "hrchyCdGrp";
  const HRCHY_NM_GRP_COL_ID = "hrchyNmGrp";
  const LOC_CD_GRP_COL_ID = "locCdGrp";
  const LOC_NM_GRP_COL_ID = "locNmGrp";
  const EXCEL_EXPORT = "excelExport";

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {locations: null, category: null});
  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [periodFilter, setPeriodFilter] = useState(
    reduxViewState?.period ?
      {start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end)}
      : INITIAL_PERIOD
  );
  const [displayName, setDisplayName] = useState(reduxViewState?.displayName ?? null);
  const [latestInvDate, setLatestInvDate] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});

  const getDisplayText = (translationKey, originTxt) => {
    if (displayName != null && displayName[translationKey]) {
      return displayName[translationKey];
    }
    return t(originTxt);
  };

  const chartSeries = useMemo(() => [
    {
      field: MEASURE.PURCHASE_PLAN_AMT,
      text: getDisplayText(MEASURE.PURCHASE_PLAN_AMT, MEASURE.PURCHASE_PLAN_AMT),
      type: "line",
      groupName: t("amt"),
      isAmt: true
    },
    {
      field: MEASURE.PURCHASE_ORDER_ACTUAL_AMT,
      text: getDisplayText(MEASURE.PURCHASE_ORDER_ACTUAL_AMT, MEASURE.PURCHASE_ORDER_ACTUAL_AMT),
      type: "bar",
      orientation: "left",
      groupName: t("amt"),
      isAmt: true
    },
    {
      field: MEASURE.PURCHASE_BUDGET_AMT,
      text: getDisplayText(MEASURE.PURCHASE_BUDGET_AMT, MEASURE.PURCHASE_BUDGET_AMT),
      type: "bar",
      orientation: "left",
      groupName: t("amt"),
      isAmt: true
    },
    {
      field: MEASURE.PURCHASE_BUDGET_AMT_CUM,
      text: getDisplayText(MEASURE.PURCHASE_BUDGET_AMT_CUM, MEASURE.PURCHASE_BUDGET_AMT_CUM),
      type: "bar",
      orientation: "left",
      groupName: t("amt"),
      isAmt: true
    },
    {
      field: MEASURE.PURCHASE_AMT_CUM,
      text: getDisplayText(MEASURE.PURCHASE_AMT_CUM, MEASURE.PURCHASE_AMT_CUM),
      type: "bar",
      orientation: "left",
      groupName: t("amt"),
      isAmt: true
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, displayName]);

  const defaultSeries = useMemo(() => [chartSeries[0], chartSeries[1]],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartSeries, displayName]);

  const getLocalizedSeries = useMemo(() => {
    const savedFields = reduxViewState?.series?.map(s => s.field) ?? defaultSeries.map(s => s.field);
    return savedFields
      .map(field => chartSeries.find(s => s.field === field))
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxViewState?.series, chartSeries, defaultSeries, displayName]);

  const [pivotConfig, setPivotConfig] = useState({
    groupKey: "measure",
    chartSeries: getLocalizedSeries
  });

  /*
   * pivotConfig.chartSeries 는 선택 시점의 시리즈 객체(=그때 언어의 번역문)를 담고 있어서, 언어를
   * 바꿔도 옛 텍스트가 남는다. 차트에 넘기기 직전 같은 field 의 최신 시리즈로 갈아끼운다.
   */
  const chartPivotConfig = useMemo(() => ({
    ...pivotConfig,
    chartSeries: (pivotConfig.chartSeries ?? []).map(row => chartSeries.find(series => series.field === row.field) ?? row)
  }), [pivotConfig, chartSeries]);

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const allMeasure = useMemo(
    () => [MEASURE.PURCHASE_ORDER_ACTUAL_AMT, MEASURE.PURCHASE_PLAN_AMT, MEASURE.PURCHASE_AMT_CUM, MEASURE.PURCHASE_BUDGET_AMT, MEASURE.PURCHASE_BUDGET_AMT_CUM],
    []
  );

  const [snackInfo, setSnackInfo] = useState({open: false, severity: "", content: ""});

  const defaultColumnDefs = [
    {
      headerName: 'vendorLocCd',
      colId: LOC_CD_GRP_COL_ID,
      width: 200,
      showRowGroup: 'vendorLocCd',
      pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"],
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {suppressCount: true},
      cellClass: 'stringType'
    },
    {
      headerName: 'vendorLocNm',
      colId: LOC_NM_GRP_COL_ID,
      width: 200,
      showRowGroup: 'vendorLocNm',
      pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"],
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {suppressCount: true}
    },
    {headerName: "originLocCd", field: "vendorLocCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true},
    {headerName: "originLocNm", field: "vendorLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true},
    {
      headerName: 'hrchyCd',
      colId: HRCHY_CD_GRP_COL_ID,
      width: 140,
      showRowGroup: 'itemHrchyCd',
      pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"],
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {suppressCount: true},
      cellClass: 'stringType'
    },
    {
      headerName: 'hrchyName',
      colId: HRCHY_NM_GRP_COL_ID,
      width: 140,
      showRowGroup: 'itemHrchyNm',
      pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"],
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {suppressCount: true}
    },
    {headerName: "hrchyCd", field: "itemHrchyCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true},
    {headerName: "hrchyNm", field: "itemHrchyNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true},
    {
      headerName: "measure", field: "measure", type: ["nonEditableColumn"], width: 200, pinned: "left", lockPinned: true,
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isClicked: setMeasureModalOpen
      },
      valueFormatter: (params) => t(params.value)
    }];

  const [columnDefs, setColumnDefs] = useState(defaultColumnDefs);

  const defaultGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({title, viewName});
    defaultSettings.defaultColDef.filter = true;
    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};

    if (rpVersionFilter) {
      params.versionId = rpVersionFilter.id;
    }

    if (filterValue?.category) {
      params.hrchyCdList = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (filterValue?.vendorLocation) {
      params.locationCd = filterValue?.vendorLocation?.map(location => location?.locCd);
    }

    if (periodFilter?.start) {
      params.startDate = periodFilter.start;
    }

    if (periodFilter?.end) {
      params.endDate = periodFilter.end;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    return {...params, ...sortParams};
  };

  const formatterByMeasure = (params, isExport = false) => {
    const {data, colDef} = params;
    const fieldName = colDef?.field
    let value = data?.[fieldName];

    if (isNull(value) || isUndefined(value) || isNaN(value)) {
      value = 0;
    }

    const amtColumns = [MEASURE.PURCHASE_AMT_CUM, MEASURE.PURCHASE_PLAN_AMT, MEASURE.PURCHASE_ORDER_ACTUAL_AMT, MEASURE.PURCHASE_BUDGET_AMT, MEASURE.PURCHASE_BUDGET_AMT_CUM];
    const display = [];
    for (const amt of amtColumns) {
      if (displayName !== null && displayName[amt] != null) {
        display.push(displayName[amt]);
      } else {
        display.push(amt);
      }
    }

    const locCd = params?.data?.vendorLocCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }

    if (display.includes(data?.measure)) {
      const formatValue = NumberUtil.formatNumber(value, {maximumFractionDigits: 2, minimumFractionDigits: 2})
      if (isExport) {
        return formatValue.replace(/,/g, "");
      }
      if (value < 0) {
        return `-${symbol}${replace(formatValue, "-", "")}`;
      }
      return symbol + formatValue;
    }

    return null;
  }
  formatterByMeasure.customType = EXCEL_EXPORT;

  const headerFormatter = (value) => {
    return format(new ZDate(value), "yyyy-MM");
  }

  const cellStyle = (params) => {
    const {colDef, data} = params;

    if (data?.latestInvDate && (new ZDate(colDef.field) > new ZDate(data.latestInvDate))) {
      return {background: green[50]};
    }
    return null;
  }

  const getExportDefaultColumnDefs = () => {
    const hasCategoryFilter = filterValue.category?.length > 0;
    let exportDefaultColumnDefs = [
      { field: "vendorLocCd", colId: LOC_CD_GRP_COL_ID, rowGroup: true },
      { field: "vendorLocNm", colId: LOC_NM_GRP_COL_ID, rowGroup: true },
      {
        headerName: "vendorLocCd",
        field: "vendorLocCd",
        showRowGroup: 'vendorLocCd',
        cellClass: 'stringType'
      },
      {
        headerName: "vendorLocNm",
        field: "vendorLocNm",
        showRowGroup: 'vendorLocNm',
        cellClass: 'stringType'
      }
    ]

    const hrchyFields = [
      {field: "itemHrchyCd", colId: HRCHY_CD_GRP_COL_ID, rowGroup: true, hide: !filterValue.category?.length > 0},
      {field: "itemHrchyNm", colId: HRCHY_NM_GRP_COL_ID, rowGroup: true, hide: !filterValue.category?.length > 0},
      {
        headerName: "hrchyCd",
        field: "itemHrchyCd",
        cellClass: 'stringType'
      },
      {
        headerName: "hrchyName",
        field: "itemHrchyNm",
        cellClass: 'stringType'
      }
    ];

    if (hasCategoryFilter) {
      exportDefaultColumnDefs = [...exportDefaultColumnDefs, ...hrchyFields];
    }

    exportDefaultColumnDefs.push({
      headerName: "measure", field: "measure", width: 200,
      valueFormatter: (params) => t(params.value)
    });

    return exportDefaultColumnDefs;
  }

  const setGridHeaders = (headers) => {
    const dimensionDefs = cloneDeep(defaultColumnDefs);
    const exportColDefs = getExportDefaultColumnDefs();

    headers.forEach((header) => {
      dimensionDefs.push({
        headerName: headerFormatter(header), field: header, width: 120, sortable: false,
        colId: GridUtils.IGNORE_P13N_COLLID + '_' + header,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        cellStyle, valueFormatter: formatterByMeasure
      });

      exportColDefs.push({
        headerName: headerFormatter(header), field: header, width: 120, sortable: false,
        colId: GridUtils.IGNORE_P13N_COLLID + '_export_' + header, valueGetter: params => formatterByMeasure(params, true)
      });
    });

    exportColumnDefs.current = exportColDefs;
    setColumnDefs(dimensionDefs);
  }

  const setGridDatas = (headers, results) => {
    const datas = [];

    const purchaseAmtPivotData = "purchaseAmtCum";
    const purchaseBudgetAmtPivotData = "purchaseBudgetAmt";
    const purchaseBudgetAmtCumPivotData = "purchaseBudgetAmtCum";
    const purchasePlanAmtPivotData = "purchasePlanAmt";
    const purchaseOrderAmtPivotData = "purchaseOrderAmt";

    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;

      for (const measureName of measure) {
        const display = displayName?.[measureName];
        const data = {...resultsData, measure: display ? display : measureName};

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case MEASURE.PURCHASE_AMT_CUM:
              data[header] = resultsData[purchaseAmtPivotData][idx];
              break;

            case MEASURE.PURCHASE_ORDER_ACTUAL_AMT:
              data[header] = resultsData[purchaseOrderAmtPivotData][idx];
              break;

            case MEASURE.PURCHASE_BUDGET_AMT:
              data[header] = resultsData[purchaseBudgetAmtPivotData][idx];
              break;

            case MEASURE.PURCHASE_BUDGET_AMT_CUM:
              data[header] = resultsData[purchaseBudgetAmtCumPivotData][idx];
              break;

            case MEASURE.PURCHASE_PLAN_AMT:
              data[header] = resultsData[purchasePlanAmtPivotData][idx];
              break;

            default:
              break;
          }
        }
        datas.push(data);
      }
    }
    return datas;
  }

  const getChartMeasureNm = (measureName) => {
    if (measureName === MEASURE.PURCHASE_AMT_CUM) {
      return "purchaseAmtCumForChart";
    }
    if (measureName === MEASURE.PURCHASE_BUDGET_AMT) {
      return "purchaseBudgetAmtForChart";
    }
    if (measureName === MEASURE.PURCHASE_BUDGET_AMT_CUM) {
      return "purchaseBudgetAmtCumForChart";
    }
    if (measureName === MEASURE.PURCHASE_PLAN_AMT) {
      return "purchasePlanAmtForChart";
    }
    if (measureName === MEASURE.PURCHASE_ORDER_ACTUAL_AMT) {
      return "purchaseOrderAmtForChart";
    }
    return measureName;
  }

  const getChartDatas = (headers, gridDatas) => {
    const chartDatas = [];
    const measure = isEmpty(measures) ? allMeasure : measures;

    for (const measureName of measure) {
      const chartMeasureNm = getChartMeasureNm(measureName);
      if (isUndefined(chartMeasureNm)) {
        continue;
      }
      const measureDatas = gridDatas.map(gridData => gridData[chartMeasureNm]);
      const display = displayName?.[measureName];
      const data = {measure: display ? display : t(measureName)};
      const maxLength = Math.max(...measureDatas.map(arr => arr.length));
      const measureData = Array.from({length: maxLength}, (_, i) =>
        measureDatas.reduce((sum, arr) => sum + (arr?.[i] ?? 0), 0)
      );
      for (let idx = 0; idx < headers.length; idx++) {
        const header = headers[idx];
        data[header] = measureData?.[idx] ?? 0;
      }
      chartDatas.push(data);
    }
    return chartDatas;
  }

  const retrieveProcurementCategoryLocation = () => {
    const params = getRequestParams();

    gridRef.current?.api?.setGridOption('loading', true);
    rpProcurementStatusService.getAll(params)
      .then(response => {
        const {pivotResult, totalPages, totalItems, message, currency} = response.data;
        if (pivotResult) {
          const {localDateHeader, data} = pivotResult;
          if (!isEmpty(currency)) {
            currencyMap.current = currency;
          }
          if (pivotResult?.data && pivotResult.data.length > 0) {
            setLatestInvDate(headerFormatter(data[0].latestInvDate));
            setGridHeaders(localDateHeader);
            setGridData(setGridDatas(localDateHeader, data));
            const headers = localDateHeader.map(header => headerFormatter(header));
            setPivotConfig({
              ...pivotConfig,
              include: headers
            })
            setChartData(getChartDatas(headers, data));
            if (!isEmpty(message)) {
              setSnackInfo({open: true, severity: snackBarMessageColor.warning, content: t(message)});
            }
          } else {
            if (!isEmpty(message)) {
              setSnackInfo({
                open: true,
                severity: snackBarMessageColor.warning,
                content: t("MSG_NoData")
              });
            } else {
              setSnackInfo({
                open: true,
                severity: snackBarMessageColor.dataEmpty,
                content: `${t("MSG_NoData")}\n${t(message)}`
              });
            }
            setGridHeaders([]);
            setGridData([]);
            setChartData([]);
            setPivotConfig({
              ...pivotConfig,
              include: []
            })
          }
        }
        setTotalPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        const {response} = e;

        if (response?.data?.errMsg) {
          setSnackInfo({
            open: true,
            severity: snackBarMessageColor.warning,
            content: t(response.data.errMsg)
          });
        }

        debug(e);
      })
      .then(() => {
        const pageState = reduxUtil.createObjectState(viewName, {currentPage, pageSize, displayName });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        const reduxState = { ...pageState, ...filterState };

        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  };

  const retrieveP13n = () => {
    return planConfigService.getMeasureDisplayNames(CodeUtil.module.RP)
      .then(response => {
        const data = response.data?.objVal;
        if (data && !isEmpty(data)) {
          setDisplayName(data);
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setGridLoading(true);
      });
  }

  const updateSeriesState = series => {
    const fieldsOnly = series.map(s => ({ field: s.field }));
    const reduxState = reduxUtil.createObjectState(viewName, { series: fieldsOnly });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  };

  const onSelectChartMeasure = newValues => {
    const selectedSeries = [];
    for (const value of newValues) {
      const index = findIndex(chartSeries, {field: value});
      if (index !== -1) {
        selectedSeries.push(chartSeries[index]);
      }
    }

    setPivotConfig({
      ...pivotConfig,
      chartSeries: selectedSeries
    });

    updateSeriesState(selectedSeries);
  }

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  useEffect(() => {
    const initializeComponent = async () => {
      await retrieveP13n();  // p13n 조회 완료 대기
    };

    initializeComponent();
  }, []);

  useEffect(() => {
    if (!isEmpty(columnDefs) && gridLoading) {
      const hideColumns = new Set();
      const visibleColumns = new Set();

      if (!filterValue.category?.length > 0) {
        hideColumns.add(HRCHY_CD_GRP_COL_ID);
        hideColumns.add(HRCHY_NM_GRP_COL_ID);
      } else {
        visibleColumns.add(HRCHY_CD_GRP_COL_ID);
        visibleColumns.add(HRCHY_NM_GRP_COL_ID);
      }

      GridUtils.setColumnDefs({...gridRef.current, columnDefs, viewName})
      gridRef.current?.columnApi?.setColumnsVisible([...visibleColumns], true);
      gridRef.current?.columnApi?.setColumnsVisible([...hideColumns], false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs, filterValue, gridData])

  useEffect(() => {
    if (!isEmpty(columnDefs) && gridLoading) {
      GridUtils.setColumnDefs({...gridRef.current, columnDefs, viewName})
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs, gridData])

  useEffect(() => {
    if (!isEmpty(measures)) {

      const defaultMeasures = map(defaultSeries, "field");
      const currentMeasures = reduxViewState?.series ? map(reduxViewState.series, "field") : defaultMeasures;

      const includedMeasure = [];
      for (const measure of currentMeasures) {
        if (measures.includes(measure)) {
          includedMeasure.push(measure);
        }
      }

      if (isEmpty(includedMeasure)) {
        onSelectChartMeasure([measures[0]]);
      } else {
        onSelectChartMeasure(includedMeasure);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measures]);


  const retrieveProcurementCategoryAll = () => {
    retrieveProcurementCategoryLocation();
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveProcurementCategoryAll();
    }
  }

  useEffect(() => {
    if (rpVersionFilter?.id && !isEmpty(measures) && gridLoading) {
      retrieveProcurementCategoryLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortParams, measures]);

  useEffect(() => {
    if (rpVersionFilter?.id && gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter?.id, filterValue, periodFilter, gridLoading]);

  const onGridReady = params => {
    GridUtils.setColumnDefs({...params, columnDefs: defaultColumnDefs, viewName, initState: true});
  };

  const onCellClicked = (params) => {
    const filterInfo = extractFilterInfo(params, t, gridRef);
    getFilterStore(viewName).setValue(filterInfo);
  }

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData, viewName]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]}/>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} label={"itemHrchy"} enableMultiSelect selectLeafs/>
        <LocationTreeFilter viewName={viewName} title={"vendorLocation"} locTypeFilters={[CodeUtil.locationType.VENDOR.cd]}
                            filterValue={filterValue?.locations?.map(location => location?.locCd)}
                            onChangeFilter={setFilterValue} inhouseFilters={[false]}/>
        <PeriodFilter viewName={viewName} period={periodFilter} setPeriod={setPeriodFilter} dateType={"month"}/>
        <Box sx={{ml: "auto"}}>
          <ExcelExportButton
            title={title}
            service={rpProcurementStatusService}
            setExcelData={setGridDatas}
            columnDefs={exportColumnDefs.current}
            params={{...getRequestParams()}}
          />
        </Box>
      </FilterContainer>      <SplitterContainer initialSizes={[0.99, 1]} collapseDirection='up'>
        <Box className="chart-area" sx={{height: "100%"}}>
          <PivotChartComposed
            pivotConfig={chartPivotConfig}
            data={chartData}
            allDataSeries={chartSeries}
            referenceLine={{x: {title: t("txnDate"), value: latestInvDate, y: null}}}
            options={{decimalPoint: true}}
            nullConvert
            activeMeasures={measures}
          />
        </Box>
        <Box className="ag-theme-balham grid-area" sx={{height: "calc(100% - 20px)", mt: 2}}>
          <AgGridReact
            ref={gridRef}
            rowData={gridData}
            onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            {...defaultGridMemo}
            onCellClicked={onCellClicked}
            statusBar={statusBar}
            groupHideOpenParents
            groupDisplayType={'custom'}
            groupDefaultExpanded={11}
            onGridReady={onGridReady}
          />
        </Box>
      </SplitterContainer>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({...snackInfo, open: false})}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
      />
    </>
  );
};

export default withTranslation()(ProcurementStatusReport);